import { Shareholding, ShareholdingTypeLabel } from '@commenda/api';
import {
  Badge,
  Button,
  CountryCode,
  DataTable,
  Dropdown,
  FlagComponent,
  Icons,
  TruncatedText,
} from '@commenda/kakashi';
import { getCountryName, OwnershipRelationshipTypeMap } from '@commenda/utils';
import React, { useCallback, useMemo, useState } from 'react';
import {
  getStakeholderColorById,
  OTHERS_STAKEHOLDER_ID,
} from '../constants';

type Column = {
  accessorKey: string;
  header: string;
  title: string;
  width?: number;
  showInSelector?: boolean;
  enableSorting?: boolean;
  cell: (info: { row: { original: Shareholding; index: number } }) => React.ReactNode;
};

interface StakeholdersTableProps {
  rows: Shareholding[];
  nounSingular: string;
  classNounSingular: string | null;
  percentScale: number;
  colorIndexById: ReadonlyMap<string, number>;
  pageSize?: number;
  onEdit: (row: Shareholding) => void;
  onDelete: (row: Shareholding) => void;
  onViewDocuments?: (row: Shareholding) => void;
  onRowHoverChange?: (stakeholdingId: string | undefined) => void;
  hoveredStakeholdingId?: string;
}

const renderPercent = (raw: number | null, scale: number): string =>
  raw === null ? '—' : `${(raw / scale).toFixed(2)}%`;

const buildColumns = (
  scale: number,
  colorIndexById: ReadonlyMap<string, number>,
  onEdit: StakeholdersTableProps['onEdit'],
  onDelete: StakeholdersTableProps['onDelete'],
  onViewDocuments: StakeholdersTableProps['onViewDocuments'],
  onMenuOpenChange: (stakeholdingId: string, open: boolean) => void,
  showStockClass: boolean,
  nounSingular: string,
  classNounSingular: string,
) => [
  {
    id: 'name',
    sticky: 'left' as const,
    columns: [
      {
        accessorKey: 'name',
        header: nounSingular,
        title: nounSingular,
        showInSelector: false,
        enableSorting: false,
        width: 280,
        cell: ({ row }) => {
          const isOthers = row.original.stakeholdingId === OTHERS_STAKEHOLDER_ID;
          return (
            <div className="flex items-center gap-md min-w-0">
              <span
                aria-hidden
                className="size-1.5 shrink-0 rounded-full"
                style={{
                  backgroundColor: getStakeholderColorById(
                    colorIndexById,
                    row.original.stakeholdingId,
                  ).base,
                }}
              />
              <TruncatedText
                className={
                  isOthers
                    ? 'txt-small-medium text-text-secondary'
                    : 'txt-small-medium text-text-primary'
                }
              >
                {row.original.name}
              </TruncatedText>
              {!isOthers && (
                <Badge variant="gray" size="sm">
                  {ShareholdingTypeLabel[row.original.type]}
                </Badge>
              )}
            </div>
          );
        },
      } satisfies Column,
    ],
  },
  {
    id: 'main',
    columns: [
      {
        accessorKey: 'dilutedOwnershipPercent',
        header: 'Ownership',
        title: 'Ownership',
        enableSorting: false,
        width: 100,
        cell: ({ row }) => (
          <span className="txt-small-regular text-text-primary">
            {renderPercent(
              row.original.dilutedOwnershipPercent ?? row.original.ownershipPercent,
              scale,
            )}
          </span>
        ),
      } satisfies Column,
      ...(showStockClass
        ? [
            {
              accessorKey: 'stockClass',
              header: classNounSingular,
              title: classNounSingular,
              enableSorting: false,
              width: 140,
              cell: ({ row }) => {
                if (row.original.stakeholdingId === OTHERS_STAKEHOLDER_ID) {
                  return <span className="text-text-quaternary">—</span>;
                }
                const sc = row.original.stockClass;
                if (!sc) return <span className="text-text-quaternary">—</span>;
                return (
                  <span className="txt-small-regular text-text-primary">
                    {sc.name}
                  </span>
                );
              },
            } satisfies Column,
          ]
        : []),
      {
        accessorKey: 'country',
        header: 'Nationality / Country',
        title: 'Nationality / Country',
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.stakeholdingId === OTHERS_STAKEHOLDER_ID) {
            return <span className="text-text-quaternary">—</span>;
          }
          const code = row.original.nationality ?? row.original.incorporationCountry;
          const name = getCountryName(code);
          if (!code || !name) return <span className="text-text-quaternary">—</span>;
          return (
            <div className="flex items-center gap-sm">
              <FlagComponent
                country={code as CountryCode}
                countryName={name}
                _className="size-4 rounded-full"
              />
              <span className="txt-small-regular text-text-primary">{name}</span>
            </div>
          );
        },
      } satisfies Column,
      {
        accessorKey: 'relationshipType',
        header: 'Relationship',
        title: 'Relationship',
        enableSorting: false,
        width: 130,
        cell: ({ row }) => {
          if (row.original.stakeholdingId === OTHERS_STAKEHOLDER_ID) {
            return <span className="text-text-quaternary">—</span>;
          }
          return (
            <span className="txt-small-regular text-text-secondary">
              {OwnershipRelationshipTypeMap[row.original.relationshipType] ?? row.original.relationshipType}
            </span>
          );
        },
      } satisfies Column,
      {
        accessorKey: 'hasSignificantControl',
        header: 'Significant control',
        title: 'Significant control',
        enableSorting: false,
        width: 140,
        cell: ({ row }) => {
          if (row.original.stakeholdingId === OTHERS_STAKEHOLDER_ID) {
            return <span className="text-text-quaternary">—</span>;
          }
          return row.original.hasSignificantControl ? (
            <Badge variant="success" size="sm">Yes</Badge>
          ) : (
            <Badge variant="warning" size="sm">No</Badge>
          );
        },
      } satisfies Column,
    ],
  },
  {
    id: 'actions',
    sticky: 'right' as const,
    columns: [
      {
        accessorKey: 'actions',
        header: '',
        title: '',
        width: 65,
        showInSelector: false,
        enableSorting: false,
        cell: ({ row }) => {
          if (row.original.stakeholdingId === OTHERS_STAKEHOLDER_ID) return null;
          return (
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              preventAutoFocusOnClose
              onOpenChange={(open) =>
                onMenuOpenChange(row.original.stakeholdingId, open)
              }
              menu={{
                groups: [
                  {
                    items: [
                      ...(onViewDocuments
                        ? [
                            {
                              label: 'View documents',
                              onClick: () => onViewDocuments(row.original),
                              icon: Icons.File04,
                            },
                          ]
                        : []),
                      {
                        label: 'Edit',
                        onClick: () => onEdit(row.original),
                        icon: Icons.Edit02,
                      },
                      {
                        label: 'Delete',
                        onClick: () => onDelete(row.original),
                        icon: Icons.Trash01,
                        labelClassName: 'text-text-error',
                        iconClassName: 'text-text-error',
                      },
                    ],
                  },
                ],
              }}
              align="end"
            >
              <Button variant="tertiary" size="sm" aria-label={`${nounSingular} actions`}>
                <Icons.DotsVertical className="size-4 text-text-quaternary" />
              </Button>
            </Dropdown>
          </div>
          );
        },
      } satisfies Column,
    ],
  },
];

export const StakeholdersTable = ({
  rows,
  nounSingular,
  classNounSingular,
  percentScale,
  colorIndexById,
  pageSize = 25,
  onEdit,
  onDelete,
  onViewDocuments,
  onRowHoverChange,
  hoveredStakeholdingId,
}: StakeholdersTableProps) => {
  // Open-kebab row reads as "active" with the hover bg, preserving visual
  // continuity from the click that opened the menu.
  const [openMenuStakeholdingId, setOpenMenuStakeholdingId] = useState<
    string | undefined
  >(undefined);
  const handleMenuOpenChange = useCallback(
    (stakeholdingId: string, open: boolean) => {
      setOpenMenuStakeholdingId(open ? stakeholdingId : undefined);
    },
    [],
  );
  // Memoized: a fresh columnGroups remounts the kebab Dropdown mid-click
  // and Radix drops the action.
  // Hide the Share class column unless at least two rows have a value —
  // a single-row column reads as a stray detail rather than a comparison.
  const showStockClass = useMemo(
    () =>
      classNounSingular !== null &&
      rows.filter((r) => r.stockClass !== null).length >= 2,
    [rows, classNounSingular],
  );
  const columnGroups = useMemo(
    () =>
      buildColumns(
        percentScale,
        colorIndexById,
        onEdit,
        onDelete,
        onViewDocuments,
        handleMenuOpenChange,
        showStockClass,
        nounSingular,
        classNounSingular ?? '',
      ),
    [
      percentScale,
      colorIndexById,
      onEdit,
      onDelete,
      onViewDocuments,
      handleMenuOpenChange,
      showStockClass,
      nounSingular,
      classNounSingular,
    ],
  );
  const handleRowMouseEnter = useCallback(
    (row: Shareholding) => onRowHoverChange?.(row.stakeholdingId),
    [onRowHoverChange],
  );
  const handleRowMouseLeave = useCallback(
    () => onRowHoverChange?.(undefined),
    [onRowHoverChange],
  );
  return (
    <div className="min-w-0">
      <DataTable
        data={rows}
        rowDataAttributes={(row) => ({
          'data-ai-item': 'Stakeholder',
          ...(row.stakeholdingId && row.stakeholdingId !== OTHERS_STAKEHOLDER_ID
            ? { 'data-ai-id': String(row.stakeholdingId) }
            : {}),
        })}
        columnGroups={columnGroups}
        paginationConfig={{
          isEnabled: rows.length > pageSize,
          type: 'PAGE' as const,
          pageSize,
          showSummary: true,
        }}
        tableRowClassName="h-[52px]"
        rowClassName={(row) =>
          row.stakeholdingId === OTHERS_STAKEHOLDER_ID ? 'bg-bg-secondary' : ''
        }
        stickyRowBgClassName={(row) =>
          row.stakeholdingId === OTHERS_STAKEHOLDER_ID ? 'bg-bg-secondary group-hover:bg-bg-primary-hover' : undefined
        }
        isRowHovered={(row) =>
          (hoveredStakeholdingId !== undefined &&
            row.stakeholdingId === hoveredStakeholdingId) ||
          row.stakeholdingId === openMenuStakeholdingId
        }
        onRowMouseEnter={onRowHoverChange ? handleRowMouseEnter : undefined}
        onRowMouseLeave={onRowHoverChange ? handleRowMouseLeave : undefined}
      />
    </div>
  );
};
